import { isRanap } from "@/utils/unit";

export interface BarisRencana {
  id?: number | null;
  nomor?: number | null;
  seq?: number | null;
  stopped_at?: string | null;
  authored_at?: string | null;
  unit_id?: string | null;
  unit?: { id: string; nama: string } | null;
  wajib_verifikasi?: boolean;
  [key: string]: any;
}

export interface GrupRencana {
  nomor: number;
  baris: BarisRencana[];
  jumlahBarisTabel: number;
}

export function unitKode(row: BarisRencana | null | undefined): string {
  if (!row) return "";
  if (row.unit && row.unit.id) return String(row.unit.id);
  return row.unit_id ? String(row.unit_id) : "";
}

export function perluVerifikasi(row: BarisRencana | null | undefined): boolean {
  if (!row) return false;
  if (typeof row.wajib_verifikasi === "boolean") return row.wajib_verifikasi;
  const kode = unitKode(row);
  if (!kode) return true;
  return isRanap(kode);
}

export function bandingRencana(a: BarisRencana, b: BarisRencana): number {
  if (a.nomor && b.nomor) {
    if (a.nomor !== b.nomor) return a.nomor - b.nomor;
    const aStop = a.stopped_at ? 1 : 0;
    const bStop = b.stopped_at ? 1 : 0;
    if (aStop !== bStop) return aStop - bStop;
    const aAt = a.authored_at || "";
    const bAt = b.authored_at || "";
    if (aAt !== bAt) return aAt < bAt ? -1 : 1;
    return (a.id || 0) - (b.id || 0);
  }

  if (a.stopped_at && !b.stopped_at) return 1;
  if (!a.stopped_at && b.stopped_at) return -1;
  if (a.stopped_at && b.stopped_at) return 0;
  return (a.seq || 0) - (b.seq || 0);
}

export function jumlahBarisDiagnosis(row: BarisRencana): number {
  const isi = Array.isArray(row.content) ? row.content : [];
  return Math.max(1, isi.length);
}

export function kelompokkan(rows: BarisRencana[]): GrupRencana[] {
  const grup: GrupRencana[] = [];
  const indeks: { [kunci: string]: number } = {};

  (rows || []).forEach((row, i) => {
    const nomor = row.nomor ? row.nomor : i + 1;
    const kunci = String(nomor);

    if (indeks[kunci] === undefined) {
      indeks[kunci] = grup.length;
      grup.push({ nomor: nomor, baris: [], jumlahBarisTabel: 0 });
    }

    const g = grup[indeks[kunci]];
    g.baris.push(row);
    g.jumlahBarisTabel += jumlahBarisDiagnosis(row);
  });

  return grup;
}

function teksPilihan(pilihan: any): string {
  if (!pilihan) return "";
  if (typeof pilihan === "object") {
    return pilihan.name ? String(pilihan.name).trim() : "";
  }
  return String(pilihan).trim();
}

function daftarFaktor(faktor: any): string {
  return (Array.isArray(faktor) ? faktor : [])
    .map(item => {
      const nama = item && item.name ? String(item.name).trim() : "";
      const pilihan = teksPilihan(item && item.option);
      return nama && pilihan ? `${nama} [${pilihan}]` : nama;
    })
    .filter(teks => !!teks)
    .join(", ");
}

export function kalimatDiagnosa(diagnosa: any): string {
  const nama = diagnosa && diagnosa.name ? String(diagnosa.name).trim() : "";
  if (!nama) return "";

  const penyebab = daftarFaktor(diagnosa.etiologi);
  const tanda = daftarFaktor(diagnosa.gejala);

  return [
    nama,
    penyebab ? `b.d. ${penyebab}` : "",
    tanda ? `d.d. ${tanda}` : ""
  ]
    .filter(bagian => !!bagian)
    .join(" ");
}

export function diagnosaAktif(rows: BarisRencana[]): string[] {
  const aktif = (rows || []).filter(row => !!row && !row.stopped_at);
  const terkini = kelompokkan(aktif.slice().sort(bandingRencana)).map(
    grup => grup.baris[grup.baris.length - 1]
  );

  const hasil: string[] = [];

  terkini.forEach(row => {
    const isi = Array.isArray(row.content) ? row.content : [];

    isi.forEach(diagnosa => {
      const kalimat = kalimatDiagnosa(diagnosa);
      if (kalimat && !hasil.includes(kalimat)) hasil.push(kalimat);
    });
  });

  return hasil;
}

export function assesmentDariDiagnosa(daftar: string[]): string {
  return (daftar || []).map((kalimat, i) => `${i + 1}. ${kalimat}`).join("\n");
}
